import { useState } from 'react'
import { Button } from '../ui/Button'
import { Badge } from '../ui/Badge'
import { Card } from '../ui/Card'
import { Input } from '../ui/Input'
import { CustomerSelector } from './CustomerSelector'
import {
  ShoppingCart,
  CreditCard,
  DollarSign,
  Smartphone,
  Receipt,
  ChevronUp,
  ChevronDown
} from 'lucide-react'

interface CartItem {
  id: string
  name: string
  quantity: number
  total: number
}

interface Customer {
  id: string
  name: string
  phone?: string
  email?: string
  gst_number?: string
}

interface MobileFloatingCheckoutProps {
  cart: CartItem[]
  cartSubtotal: number
  taxAmount: number
  finalTotal: number
  formatCurrency: (amount: number) => string
  paymentMethod: 'cash' | 'card' | 'upi' | 'split'
  setPaymentMethod: (method: 'cash' | 'card' | 'upi' | 'split') => void
  amountReceived: string
  setAmountReceived: (amount: string) => void
  onProcessSale: () => void
  canCompleteSale: boolean
  onPreviewBill: () => void
  // Customer props
  customers: Customer[]
  selectedCustomer: Customer | null
  onCustomerChange: (customerId: string) => void
  onAddCustomer: () => void
}

export const MobileFloatingCheckout = ({
  cart,
  cartSubtotal,
  taxAmount,
  finalTotal,
  formatCurrency,
  paymentMethod,
  setPaymentMethod,
  amountReceived,
  setAmountReceived,
  onProcessSale,
  canCompleteSale,
  onPreviewBill,
  customers,
  selectedCustomer,
  onCustomerChange,
  onAddCustomer
}: MobileFloatingCheckoutProps) => {
  const [isExpanded, setIsExpanded] = useState(false)
  
  const change = paymentMethod === 'cash' ? Math.max(0, parseFloat(amountReceived || '0') - finalTotal) : 0

  if (cart.length === 0) return null

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 shadow-lg">
      {/* Collapsed State */}
      {!isExpanded && (
        <div className="p-3">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <div className="bg-orange-100 dark:bg-orange-900/30 p-1.5 rounded-full">
                <ShoppingCart className="h-4 w-4 text-orange-600 dark:text-orange-400" />
              </div>
              <div>
                <div className="text-xs text-gray-600 dark:text-gray-400">{cart.length} items</div>
                <div className="text-lg font-bold text-gray-900 dark:text-gray-100">
                  {formatCurrency(finalTotal)}
                </div>
              </div>
            </div>
            
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsExpanded(true)}
              className="p-1 h-8 w-8"
            >
              <ChevronUp className="h-4 w-4" />
            </Button>
          </div>
          
          <Button
            onClick={onProcessSale}
            disabled={!canCompleteSale}
            className="w-full h-9 text-sm font-semibold"
          >
            Complete Sale
          </Button>
        </div>
      )}

      {/* Expanded State */}
      {isExpanded && (
        <div className="max-h-80 overflow-y-auto">
          <div className="p-3 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                <ShoppingCart className="h-4 w-4" />
                Checkout
              </h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsExpanded(false)}
                className="p-2"
              >
                <ChevronDown className="h-5 w-5" />
              </Button>
            </div>
            
            {/* Order Summary */}
            <div className="space-y-1 mb-3">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600 dark:text-gray-400">Subtotal</span>
                <span className="text-gray-900 dark:text-gray-100">{formatCurrency(cartSubtotal)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600 dark:text-gray-400">Tax</span>
                <span className="text-gray-900 dark:text-gray-100">{formatCurrency(taxAmount)}</span>
              </div>
              <div className="flex justify-between text-lg font-semibold border-t border-gray-200 dark:border-gray-700 pt-2">
                <span className="text-gray-900 dark:text-gray-100">Total</span>
                <span className="text-gray-900 dark:text-gray-100">{formatCurrency(finalTotal)}</span>
              </div>
            </div>

            {/* Customer Selection */}
            <div className="space-y-2 mb-3">
              <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">Customer</h4>
              <CustomerSelector
                customers={customers}
                selectedCustomer={selectedCustomer}
                onCustomerChange={onCustomerChange}
                onAddCustomer={onAddCustomer}
                className="w-full"
                hideLabel={true}
              />
            </div>

            {/* Payment Methods */}
            <div className="space-y-2 mb-3">
              <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">Payment Method</h4>
              <div className="grid grid-cols-3 gap-2">
                <Button
                  variant={paymentMethod === 'cash' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setPaymentMethod('cash')}
                  className="flex items-center gap-1 h-10 text-xs"
                >
                  <DollarSign className="h-4 w-4" />
                  Cash
                </Button>
                <Button
                  variant={paymentMethod === 'card' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setPaymentMethod('card')}
                  className="flex items-center gap-1 h-10 text-xs"
                >
                  <CreditCard className="h-4 w-4" />
                  Card
                </Button>
                <Button
                  variant={paymentMethod === 'upi' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setPaymentMethod('upi')}
                  className="flex items-center gap-1 h-10 text-xs"
                >
                  <Smartphone className="h-4 w-4" />
                  UPI
                </Button>
              </div>
            </div>

            {/* Cash Payment Details */}
            {paymentMethod === 'cash' && (
              <div className="space-y-2 mb-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Amount Received
                  </label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={amountReceived}
                    onChange={(e) => setAmountReceived(e.target.value)}
                    placeholder={formatCurrency(finalTotal)}
                    className="h-10 text-base"
                  />
                </div>
                
                {change > 0 && (
                  <div className="bg-green-50 dark:bg-green-900/20 p-3 rounded-lg">
                    <div className="flex justify-between items-center">
                      <span className="text-green-700 dark:text-green-300 font-medium">Change to Return:</span>
                      <span className="text-lg font-bold text-green-700 dark:text-green-300">
                        {formatCurrency(change)}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={onPreviewBill}
                className="flex-1 h-10 flex items-center justify-center gap-1 text-sm"
              >
                <Receipt className="h-4 w-4" />
                Preview
              </Button>
              <Button
                onClick={onProcessSale}
                disabled={!canCompleteSale}
                className="flex-1 h-9 text-sm font-semibold"
              >
                Complete Sale
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}